import axios from 'axios'
import { authApi } from './endpoints'

const API_BASE_URL = '/api'

const TOKEN_KEY = 'access_token'
const REFRESH_TOKEN_KEY = 'refresh_token'

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
})

let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (error: any) => void
}> = []

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token!)
    }
  })
  failedQueue = []
}

function requestUrl(config: { url?: string; baseURL?: string } | undefined): string {
  return `${config?.baseURL || ''}${config?.url || ''}`
}

function isPublicAuthRequest(config: { url?: string; baseURL?: string } | undefined): boolean {
  const url = requestUrl(config)
  return (
    url.includes('/auth/admin-login') ||
    url.includes('/auth/telegram') ||
    url.includes('/auth/login') ||
    url.includes('/auth/register') ||
    url.includes('/auth/refresh')
  )
}

apiClient.interceptors.request.use(
  (config) => {
    // Admin login is username/password only — never attach a Mini App user JWT.
    if (isPublicAuthRequest(config)) {
      if (config.headers) {
        delete (config.headers as Record<string, unknown>).Authorization
        delete (config.headers as Record<string, unknown>).authorization
      }
      return config
    }
    const token = localStorage.getItem(TOKEN_KEY)
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Wrong admin password is a normal 401 — do not treat it as an expired user session.
    if (error.response?.status === 401 && isPublicAuthRequest(originalRequest)) {
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (originalRequest.url?.includes('/auth/refresh')) {
        clearAuthStorage()
        window.location.href = '/register'
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return apiClient(originalRequest)
          })
          .catch(err => {
            return Promise.reject(err)
          })
      }

      originalRequest._retry = true
      isRefreshing = true

      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
      if (!refreshToken) {
        clearAuthStorage()
        window.location.href = '/register'
        return Promise.reject(error)
      }

      try {
        const res = await authApi.refresh(refreshToken)
        const { token, refresh_token } = res
        localStorage.setItem(TOKEN_KEY, token)
        if (refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token)

        processQueue(null, token)
        originalRequest.headers.Authorization = `Bearer ${token}`
        return apiClient(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        clearAuthStorage()
        window.location.href = '/register'
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    if (error.response?.status === 401) {
      clearAuthStorage()
    }

    return Promise.reject(error)
  }
)

function clearAuthStorage(): void {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem('user_id')
}

export default apiClient
